import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { Card, Col, FormControl, Row, Table } from 'react-bootstrap';
import { MultiSelect } from 'react-multi-select-component';
import { toast } from 'react-toastify';
import DropDownInput from '../../form-generator/components/DropDownInput';
import { makeApiRequests } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import DateRangePicker from '../DateRangePicker';
import Loader from '../Loader';
import Toggler from '../Toggler';

const getInitialDateRange = () => {
  const today = new Date();
  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - 2);
  return { startDate: startDate.toISOString(), endDate: today.toISOString() };
};

const toDayBounds = range => {
  if (!range?.startDate || !range?.endDate) return null;
  const start = new Date(range.startDate);
  const end = new Date(range.endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return { start: start.toISOString(), end: end.toISOString() };
};

const CONTRACT_RETURN_PATHS = [
  'setup',
  'salesLocation',
  'lender',
  'inventoryUnit',
  'salesPrice',
  'buyer',
  'propertyAddress',
  'propertyCity',
  'propertyState',
  'zipCode',
  'propertyCounty',
  'status',
  'applicationNumber'
];

const INVENTORY_RETURN_PATHS = [
  'manufacturer',
  'floorplanLender',
  'model',
  'sizeOverall',
  'year',
  'serialA',
  'exteriorSiding',
  'invoice',
  'invoiceDate'
];

const SelectionBox = ({
  selectedType,
  selectedDateRange,
  selectedSalesLocation,
  typeOptions,
  salesLocations,
  onTypeChange,
  onLocationChange,
  onDateRangeChange,
  manufacturerOptions,
  lenderOptions,
  selectedManufacturer,
  selectedLender,
  setSelectedManufacturer,
  setSelectedLender,
  setModalText
}) => {
  const handleManufacturerChange = selectedItems => {
    const selectedIds = selectedItems.map(item => item.value);
    setSelectedManufacturer(selectedIds);
  };

  const handleLenderChange = selectedItems => {
    const selectedIds = selectedItems.map(item => item.value);
    setSelectedLender(selectedIds);
  };

  return (
    <>
      <Card className="border rounded">
        <Card.Header className="bg-primary">
          <h5 className="mb-0 text-white">Delivery Setup Reports</h5>
        </Card.Header>
        <Card.Body className="py-3">
          <Row>
            <Col xs={12} md={2} className="px-4 mt-3">
              <h6 className="mb-2">Report Type</h6>
              <DropDownInput
                id="reportTypeSelect"
                size={'sm'}
                value={selectedType}
                options={typeOptions}
                onChangeFunction={onTypeChange}
                required
              />
            </Col>
            <Col xs={12} md={2} className="px-4 mt-3">
              <h6 className="mb-2">Sales Location</h6>
              <DropDownInput
                id="locationSelect"
                size={'sm'}
                value={selectedSalesLocation._id}
                options={salesLocations.map(l => l.location)}
                optionValues={salesLocations.map(l => l._id)}
                onChangeFunction={onLocationChange}
                required
              />
            </Col>
            <Col xs={12} md={3} className="px-4 mt-3">
              <h6 className="mb-2">Lender</h6>
              <MultiSelect
                showCheckbox
                closeOnChangedValue={false}
                id="lender"
                size="sm"
                value={selectedLender.map(id => ({
                  label: lenderOptions.find(m => m.value === id)?.label || '',
                  value: id
                }))}
                options={lenderOptions}
                onChange={handleLenderChange}
                required
              />
            </Col>
            <Col xs={12} md={3} className="px-4 mt-3">
              <h6 className="mb-2">Manufacturer</h6>
              <MultiSelect
                showCheckbox
                closeOnChangedValue={false}
                id="manufacturer"
                size="sm"
                value={selectedManufacturer.map(id => ({
                  label: manufacturerOptions.find(m => m.value === id)?.label || '',
                  value: id
                }))}
                options={manufacturerOptions}
                onChange={handleManufacturerChange}
                required
              />
            </Col>
            <Col xs={12} md={2} className="px-4 mt-3">
              <h6 className="mb-2">Model</h6>
              <FormControl placeholder="Enter here..." onChange={e => setModalText(e.target.value)} />
            </Col>
            {selectedType === 'Monthly' && (
              <Col xs={12} className="px-4 mt-3">
                <h6 className="mb-2">Select Date Range</h6>
                <DateRangePicker defaultDates={selectedDateRange} onDateChange={onDateRangeChange} />
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};

const DeliverySetupReports = ({ appChoices }) => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeOptions] = useState(['Monthly', 'Annual']);
  const [selectedType, setSelectedType] = useState('Monthly');
  const [selectedManufacturer, setSelectedManufacturer] = useState([]);
  const [selectedLender, setSelectedLender] = useState([]);
  const [modelText, setModalText] = useState('');

  const [salesLocations] = useState([
    { location: 'All', _id: 'All' },
    ...appChoices.find(ac => ac.key === 'salesLocation').values
  ]);

  const [selectedSalesLocation, setSelectedSalesLocation] = useState(salesLocations[0]);
  const [selectedDateRange, setSelectedDateRange] = useState(getInitialDateRange());
  const [exteriorSidingOptions] = useState(appChoices.find(ac => ac.key === 'Exterior Siding').values);

  const manufacturerOptions = useMemo(
    () =>
      (appChoices.find(ac => ac.key === 'manufacturer')?.values || [])
        .filter(m => m?._id)
        .map(m => ({ label: m.name || m.factoryShortName || 'Unknown', value: m._id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [appChoices]
  );

  const lenderOptions = useMemo(
    () =>
      (appChoices.find(ac => ac.key === 'lender')?.values || [])
        .filter(l => l?._id)
        .map(l => ({ label: l.shortName || l.lienholderCorporateName || l.name || 'Unknown', value: l._id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [appChoices]
  );

  const bounds = toDayBounds(selectedDateRange);
  const dateRangeKey = bounds ? `${bounds.start}|${bounds.end}` : '';

  useEffect(() => {
    if (!dateRangeKey) {
      setLoading(false);
      setContracts([]);
      return undefined;
    }

    const [start, end] = dateRangeKey.split('|');
    let cancelled = false;
    setLoading(true);

    const timer = setTimeout(async () => {
      const { response, error } = await makeApiRequests({
        requestBody: {
          sort: '-setup.start',
          lean: true,
          filter: {
            'setup.start': { $gte: start, $lte: end }
          },
          returnPaths: CONTRACT_RETURN_PATHS,
          inventoryReturnPaths: INVENTORY_RETURN_PATHS
        },
        endpoint: ENDPOINTS.CONTRACTS_SEARCH_NEW
      });

      if (cancelled) return;

      setLoading(false);
      if (error) {
        toast.error(error);
        return;
      }

      setContracts(
        (response?.results || [])
          .filter(c => c.salesLocation)
          .map(c => {
            const { inventoryUnit } = c;
            if (!inventoryUnit) return c;

            inventoryUnit.inventoryId = inventoryUnit._id;
            delete inventoryUnit._id;
            return { ...c, ...inventoryUnit };
          })
      );
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [dateRangeKey]);

  const filteredContracts = useMemo(() => {
    if (selectedType === 'Annual') return [];

    let tempFilter =
      selectedSalesLocation._id === 'All'
        ? contracts
        : contracts.filter(c => c.salesLocation._id === selectedSalesLocation._id);

    const bounds = toDayBounds(selectedDateRange);
    if (bounds) {
      const startDate = new Date(bounds.start).getTime();
      const endDate = new Date(bounds.end).getTime();

      tempFilter = tempFilter.filter(c => {
        const dateSetup = new Date(c.setup?.start);
        return dateSetup.getTime() >= startDate && dateSetup.getTime() <= endDate;
      });
    }

    if (selectedManufacturer.length > 0) {
      tempFilter = tempFilter.filter(c => selectedManufacturer.includes(c.inventoryUnit?.manufacturer?._id));
    }

    if (selectedLender.length > 0) {
      tempFilter = tempFilter.filter(c => selectedLender.includes(c.lender?._id));
    }

    if (modelText !== '') {
      tempFilter = tempFilter.filter(
        c =>
          c.model?.modelName?.toLowerCase().includes(modelText.toLowerCase()) ||
          c.model?.modelNumber?.toLowerCase().includes(modelText.toLowerCase())
      );
    }

    return tempFilter.map(c => {
        const salesPrice = c['salesPrice'] || 0;
        const invoice = c['invoice'] || 0;

        return {
          _id: c['_id'],
          inventoryId: c['inventoryId'],
          Status: c['status'],
          dateOfSale: moment(c.setup.start).format('MMMM Do YYYY'),
          invoiceDate: c.invoiceDate ? moment(c.invoiceDate).format('MMMM Do YYYY') : '',
          applicationNumber: c.applicationNumber || '',
          'Size Overall': c['sizeOverall'],
          Year: c['year'],
          'Floorplan Lender': c['floorplanLender'] ? c['floorplanLender'].name : '',
          Lender: c['lender'] ? c['lender'].lienholderCorporateName : '',
          Manufacturer: c['manufacturer'] ? c['manufacturer'].name : '',
          'Serial # A': c['serialA'],
          Buyer: c['buyer'],
          'Type of Unit': 'MH',
          Invoice: invoice,
          'Property Address': c['propertyAddress'],
          'Property City': c['propertyCity'],
          'Property State': c['propertyState'],
          'Zip Code': c['zipCode'],
          'Property County': c['propertyCounty'],
          'Exterior Siding': c['exteriorSiding'],
          'Sales Price': salesPrice,
          Model: c['model']?.modelName || c['model']?.modelNumber || ''
        };
      });
  }, [
    contracts,
    selectedSalesLocation,
    selectedType,
    selectedDateRange,
    selectedManufacturer,
    modelText,
    selectedLender
  ]);

  const onTypeChange = () => {
    const type = document.getElementById('reportTypeSelect').value;
    setSelectedType(type);
  };

  const onLocationChange = () => {
    const locationId = document.getElementById('locationSelect').value;
    setSelectedSalesLocation(salesLocations.find(l => l._id === locationId));
  };

  const updateContract = (contract, field, value) => {
    const existingContract = contracts.find(c => c['_id'] === contract['_id']);
    if (existingContract) {
      existingContract[field] = value;
    }

    setContracts([...contracts]);
  };

  return (
    <div className="px-0 px-md-3 py-4">
      <SelectionBox
        selectedDateRange={selectedDateRange}
        selectedSalesLocation={selectedSalesLocation}
        selectedType={selectedType}
        typeOptions={typeOptions}
        salesLocations={salesLocations}
        onTypeChange={onTypeChange}
        onLocationChange={onLocationChange}
        onDateRangeChange={setSelectedDateRange}
        manufacturerOptions={manufacturerOptions}
        selectedManufacturer={selectedManufacturer}
        setSelectedManufacturer={setSelectedManufacturer}
        setModalText={setModalText}
        lenderOptions={lenderOptions}
        selectedLender={selectedLender}
        setSelectedLender={setSelectedLender}
      />
      <hr />

      {selectedType === 'Monthly' &&
        (loading ? (
          <Loader />
        ) : (
          <>
            <h6>Preview Report:</h6>
              <Table responsive bordered className="text-center midFont">
                <tr style={{ fontSize: 14 }} className="bg-dark text-light">
                  {/* <th>Print?</th> */}
                  <th style={{ minWidth: 100 }}>Setup Start Date</th>
                  <th>Exterior Siding</th>
                  <th>Size Overall</th>
                  <th>Floorplan Lender</th>
                  <th>Model</th>
                  <th>Lender</th>
                  <th>Make</th>
                  <th>Unit of Manufactured Housing Identification/Serial Number</th>
                  <th>Purchaser's Name</th>
                  <th>Sales Price</th>
                  <th>Invoice</th>
                  <th>Invoice Date</th>
                  <th>Property Address</th>
                  <th>City</th>
                  <th>Application Number</th>
                </tr>
                {filteredContracts.length > 0 ? (
                  filteredContracts.map(c => (
                    <tr key={c['_id']}>
                      {/* <td>
                        <FormControl
                          checked={!excludedContracts.includes(c['_id'])}
                          style={{ width: 16, height: 16 }}
                          type="checkbox"
                          onChange={e => onExcludeContractChange(c['_id'], e.target.checked)}
                        />
                      </td> */}
                      <td>{c['dateOfSale']}</td>
                      <td>
                        {c['inventoryId'] ? (
                          <Toggler
                            onFieldUpdate={(field, value) => updateContract(c, field, value)}
                            fieldName="exteriorSiding"
                            value={c['Exterior Siding']}
                            options={exteriorSidingOptions}
                            toggleEndpoint={ENDPOINTS.INVENTORY_WITH_ID(c['inventoryId'])}
                          />
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>{c['Size Overall']}</td>
                      <td>{c['Floorplan Lender']}</td>
                      <td>{c['Model']}</td>
                      <td>{c['Lender']}</td>
                      <td>{c['Manufacturer']}</td>
                      <td>{c['Serial # A']}</td>
                      <td>{c['Buyer']}</td>
                      <td>${c['Sales Price']}</td>
                      <td>${c['Invoice']}</td>
                      <td>{c['invoiceDate']}</td>
                      <td>{c['Property Address']}</td>
                      <td>{c['Property City']}</td>
                      <td>{c['applicationNumber']}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={13}>No contracts to show</td>
                  </tr>
                )}
              </Table>
              <h6 className="text-right text-muted">
                Total Reports: <span className="text-dark">{filteredContracts.length}</span>
              </h6>
            </>
          ))}
    </div>
  );
};

export default DeliverySetupReports;
